/**
 * AddEventsModal Component
 *
 * Modal for selecting economic events to track during the pre-trading ritual.
 * Displays available events from the API with filters for country and impact level.
 *
 * Features:
 * - Country filter with search, Select All, Clear All, collapsible
 * - Impact level filter (Low/Medium/High), collapsible
 * - Event list with checkboxes for selection
 * - Already-tracked events shown as disabled
 * - Selection persists across filter changes
 * - "Show selected only" toggle to review picks
 * - "Track Selected" and "Track All Visible" actions
 * - State resets on modal open to reflect latest settings
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  CheckboxGroup,
  Checkbox,
  ScrollShadow,
  Input,
  Chip,
  Card,
  CardHeader,
  CardBody,
} from '@enfomentis/ui';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { COUNTRY_NAMES, COUNTRY_TO_CURRENCY } from '@/constants/economicCalendar';
import { getCountryFlagEmoji } from '@/utils/economicCalendar';
import { formatTime } from '@/utils/dateHelpers';
import type { EconomicEventDraft } from '@/types/transient/EconomicEventDraft';

// =============================================================================
// Types
// =============================================================================

/** Valid impact level values */
type ImpactLevel = 'low' | 'medium' | 'high';

export interface AddEventsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Available events from API (cached in parent) */
  availableEvents: EconomicEventDraft[];
  /** Set of providerEventIds that are already tracked */
  trackedEventIds: Set<string>;
  /** Initial country filters from settings */
  initialCountryFilters: string[];
  /** Initial impact levels from settings */
  initialImpactLevels: ImpactLevel[];
  /** Callback when user tracks events */
  onTrackEvents: (events: EconomicEventDraft[]) => void;
  /** User's display timezone */
  displayTimezone: string;
  /** User's time format preference */
  timeFormat: '12h' | '24h';
  /** Session date in YYYY-MM-DD format (for display) */
  sessionDate: string;
}

interface CountryOption {
  value: string;
  label: string;
  searchText: string;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Build country options array from COUNTRY_TO_CURRENCY mapping
 * Format: "🇺🇸 United States (USD)" - flag emoji + full name + currency
 * Sorted A-Z by country code for deterministic order
 */
const COUNTRY_OPTIONS: CountryOption[] = Object.entries(COUNTRY_TO_CURRENCY)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([countryCode, currency]) => {
    const countryName = COUNTRY_NAMES[countryCode as keyof typeof COUNTRY_NAMES] ?? countryCode;
    const flag = getCountryFlagEmoji(countryCode);
    return {
      value: countryCode,
      label: `${flag} ${countryName} (${currency})`,
      // Search text includes code, name, and currency for comprehensive matching
      searchText: `${countryCode} ${countryName} ${currency}`.toLowerCase(),
    };
  });

/**
 * Impact level options for checkbox display
 */
const IMPACT_LEVEL_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
] as const;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Deduplicate events by providerEventId
 * - First occurrence wins (preserves order)
 * - Skips entries without providerEventId
 */
function dedupeByProviderEventId(events: EconomicEventDraft[]): EconomicEventDraft[] {
  const seen = new Set<string>();
  return events.filter((e) => {
    if (!e.providerEventId) return false;
    if (seen.has(e.providerEventId)) return false;
    seen.add(e.providerEventId);
    return true;
  });
}

// =============================================================================
// Sub-Components
// =============================================================================

/**
 * Displays impact level as a color-coded chip
 */
const ImpactBadge: React.FC<{ level: ImpactLevel | undefined }> = ({ level }) => {
  if (!level) return null;

  const colorMap: Record<ImpactLevel, 'default' | 'warning' | 'danger'> = {
    low: 'default',
    medium: 'warning',
    high: 'danger',
  };

  return (
    <Chip size="sm" variant="flat" color={colorMap[level]}>
      {level.charAt(0).toUpperCase() + level.slice(1)}
    </Chip>
  );
};

/**
 * Props for FilterSummary
 */
interface FilterSummaryProps {
  /** Selected impact levels */
  impactLevels: ImpactLevel[];
  /** Selected country filters */
  countryFilters: string[];
}

/**
 * Compact visual summary of active filters
 * Shows colored dots for impact levels + country count
 */
const FilterSummary: React.FC<FilterSummaryProps> = ({ impactLevels, countryFilters }) => {
  // Impact level colors (high=red, medium=amber, low=gray)
  const hasHigh = impactLevels.includes('high');
  const hasMedium = impactLevels.includes('medium');
  const hasLow = impactLevels.includes('low');

  // Country display: count if >1, code if 1, nothing if empty (all)
  const countryDisplay = countryFilters.length === 0
    ? null
    : countryFilters.length === 1
      ? countryFilters[0]
      : `${countryFilters.length}`;

  // If no filters active, show nothing
  if (!hasHigh && !hasMedium && !hasLow && !countryDisplay) {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5 text-xs text-default-500 flex-shrink-0">
      {/* Impact dots */}
      <div className="flex items-center gap-1">
        {hasHigh && (
          <span
            className="w-2 h-2 rounded-full bg-danger-500"
            aria-label="High impact"
          />
        )}
        {hasMedium && (
          <span
            className="w-2 h-2 rounded-full bg-warning-500"
            aria-label="Medium impact"
          />
        )}
        {hasLow && (
          <span
            className="w-2 h-2 rounded-full bg-default-400"
            aria-label="Low impact"
          />
        )}
      </div>
      {/* Country indicator */}
      {countryDisplay && (
        <>
          <span className="text-default-300">•</span>
          <span>{countryDisplay}</span>
        </>
      )}
    </div>
  );
};

/**
 * Props for EventListItem
 */
interface EventListItemProps {
  event: EconomicEventDraft;
  isSelected: boolean;
  isTracked: boolean;
  onToggle: () => void;
  displayTimezone: string;
  timeFormat: '12h' | '24h';
}

/**
 * Single event item in the selection list
 */
const EventListItem: React.FC<EventListItemProps> = ({
  event,
  isSelected,
  isTracked,
  onToggle,
  displayTimezone,
  timeFormat,
}) => {
  // Format time
  const formattedTime = useMemo(() => {
    if (!event.datetime) return '--:--';
    try {
      return formatTime(event.datetime, displayTimezone, timeFormat);
    } catch {
      return '--:--';
    }
  }, [event.datetime, displayTimezone, timeFormat]);

  // Get currency for country
  const currency = event.country
    ? COUNTRY_TO_CURRENCY[event.country.toUpperCase() as keyof typeof COUNTRY_TO_CURRENCY]
    : undefined;

  const countryDisplay = event.country
    ? currency
      ? `${event.country} (${currency})`
      : event.country
    : undefined;

  return (
    <div
      className={`flex items-center gap-3 py-2 px-2 rounded-md ${
        isTracked ? 'opacity-60 bg-default-100' : 'hover:bg-default-50'
      }`}
    >
      <Checkbox
        isSelected={isSelected}
        isDisabled={isTracked}
        onValueChange={onToggle}
        size="sm"
      />
      <div className="flex-shrink-0 w-14 text-xs font-mono text-default-500">
        {formattedTime}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm truncate ${isTracked ? 'text-default-400' : 'text-default-700'}`}>
            {event.name || 'Untitled event'}
          </span>
          {countryDisplay && (
            <span className="text-xs text-default-400 hidden sm:inline">
              {countryDisplay}
            </span>
          )}
        </div>
        {isTracked && (
          <span className="text-xs text-default-400 italic">Already tracked</span>
        )}
      </div>
      <div className="flex-shrink-0">
        <ImpactBadge level={event.impactLevel} />
      </div>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const AddEventsModal: React.FC<AddEventsModalProps> = ({
  isOpen,
  onClose,
  availableEvents,
  trackedEventIds,
  initialCountryFilters,
  initialImpactLevels,
  onTrackEvents,
  displayTimezone,
  timeFormat,
  sessionDate,
}) => {
  // ---------------------------------------------------------------------------
  // Filter State
  // ---------------------------------------------------------------------------

  const [localCountryFilters, setLocalCountryFilters] = useState<string[]>(initialCountryFilters);
  const [localImpactLevels, setLocalImpactLevels] = useState<ImpactLevel[]>(initialImpactLevels);
  const [searchQuery, setSearchQuery] = useState('');
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [showSelectedCountriesOnly, setShowSelectedCountriesOnly] = useState(false);

  // ---------------------------------------------------------------------------
  // Collapsible State (default: collapsed to focus on events)
  // ---------------------------------------------------------------------------

  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);

  // ---------------------------------------------------------------------------
  // Selection State
  // ---------------------------------------------------------------------------

  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());

  // ---------------------------------------------------------------------------
  // Refs
  // ---------------------------------------------------------------------------

  /** Track previous isOpen value for detecting open transition */
  const prevIsOpenRef = useRef(isOpen);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  /**
   * Reset modal state when opening (transition from closed to open)
   * Uses ref to detect transition, not just current value
   */
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    // Only reset on transition to open (false -> true)
    if (!wasOpen && isOpen) {
      setLocalCountryFilters(initialCountryFilters);
      setLocalImpactLevels(initialImpactLevels);
      setSearchQuery('');
      setEventSearchQuery('');
      setSelectedEventIds(new Set());
      setShowSelectedOnly(false);
      setShowSelectedCountriesOnly(false);
      setIsFiltersCollapsed(true);
    }
  }, [isOpen, initialCountryFilters, initialImpactLevels]);

  /**
   * Auto-reset showSelectedOnly when event selection becomes empty
   * Prevents user being trapped in empty "show selected" view
   */
  useEffect(() => {
    if (selectedEventIds.size === 0 && showSelectedOnly) {
      setShowSelectedOnly(false);
    }
  }, [selectedEventIds.size, showSelectedOnly]);

  /**
   * Auto-reset showSelectedCountriesOnly when country selection becomes empty
   * Same pattern as events trap fix
   */
  useEffect(() => {
    if (localCountryFilters.length === 0 && showSelectedCountriesOnly) {
      setShowSelectedCountriesOnly(false);
    }
  }, [localCountryFilters.length, showSelectedCountriesOnly]);

  /**
   * Auto-untoggle showSelectedCountriesOnly when country search becomes non-empty
   * Does NOT auto-re-enable when search is cleared (user must manually re-enable)
   */
  useEffect(() => {
    if (searchQuery.trim().length > 0 && showSelectedCountriesOnly) {
      setShowSelectedCountriesOnly(false);
    }
  }, [searchQuery, showSelectedCountriesOnly]);

  /**
   * Auto-untoggle showSelectedOnly when event search becomes non-empty
   * Does NOT auto-re-enable when search is cleared (user must manually re-enable)
   */
  useEffect(() => {
    if (eventSearchQuery.trim().length > 0 && showSelectedOnly) {
      setShowSelectedOnly(false);
    }
  }, [eventSearchQuery, showSelectedOnly]);

  // ---------------------------------------------------------------------------
  // Derived Values
  // ---------------------------------------------------------------------------

  // Filter country options by search query
  const filteredCountryOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return COUNTRY_OPTIONS;
    }
    const query = searchQuery.toLowerCase().trim();
    return COUNTRY_OPTIONS.filter((option) => option.searchText.includes(query));
  }, [searchQuery]);

  // Further filter country options for "show selected" mode
  const filteredCountryOptionsDisplay = useMemo(() => {
    if (!showSelectedCountriesOnly) {
      return filteredCountryOptions;
    }
    const selectedSet = new Set(localCountryFilters);
    return filteredCountryOptions.filter((option) => selectedSet.has(option.value));
  }, [filteredCountryOptions, showSelectedCountriesOnly, localCountryFilters]);

  // Filter available events to only those with providerEventId (selectable)
  const selectableEvents = useMemo(() => {
    return availableEvents.filter((e) => !!e.providerEventId);
  }, [availableEvents]);

  // Apply filters to get visible events
  const filteredEvents = useMemo(() => {
    let filtered = selectableEvents;

    // Apply country filter (empty = show all)
    if (localCountryFilters.length > 0) {
      const countrySet = new Set(localCountryFilters.map((c) => c.toUpperCase()));
      filtered = filtered.filter((e) => e.country && countrySet.has(e.country.toUpperCase()));
    }

    // Apply impact filter (empty = show none per schema semantics)
    if (localImpactLevels.length === 0) {
      filtered = [];
    } else {
      const impactSet = new Set<string>(localImpactLevels);
      filtered = filtered.filter((e) => e.impactLevel && impactSet.has(e.impactLevel));
    }

    // Apply event name search filter
    if (eventSearchQuery.trim()) {
      const query = eventSearchQuery.toLowerCase().trim();
      filtered = filtered.filter((e) => e.name && e.name.toLowerCase().includes(query));
    }

    // Apply "show selected only" filter
    if (showSelectedOnly) {
      filtered = filtered.filter((e) => e.providerEventId && selectedEventIds.has(e.providerEventId));
    }

    // Sort by datetime ascending (defensive copy + NaN guard)
    return [...filtered].sort((a, b) => {
      const aTime = a.datetime ? new Date(a.datetime).getTime() : Infinity;
      const bTime = b.datetime ? new Date(b.datetime).getTime() : Infinity;
      // Guard against NaN from invalid datetime strings
      const aValid = Number.isFinite(aTime) ? aTime : Infinity;
      const bValid = Number.isFinite(bTime) ? bTime : Infinity;
      if (aValid === bValid) return 0;
      return aValid - bValid;
    });
  }, [selectableEvents, localCountryFilters, localImpactLevels, eventSearchQuery, showSelectedOnly, selectedEventIds]);

  // Count of visible non-tracked events (for "Track All Visible" button)
  const visibleNonTrackedCount = useMemo(() => {
    return filteredEvents.filter((e) => e.providerEventId && !trackedEventIds.has(e.providerEventId)).length;
  }, [filteredEvents, trackedEventIds]);

  // Count how many visible country options are selected
  const visibleSelectedCountryCount = filteredCountryOptionsDisplay.filter((o) =>
    localCountryFilters.includes(o.value)
  ).length;

  // Format session date for display (safe parsing to avoid timezone shift)
  const formattedSessionDate = useMemo(() => {
    if (!sessionDate) return '';
    // Parse YYYY-MM-DD safely without timezone shift
    const [year, month, day] = sessionDate.split('-').map(Number);
    if (!year || !month || !day) return sessionDate;
    const date = new Date(Date.UTC(year, month - 1, day));
    return new Intl.DateTimeFormat(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }, [sessionDate]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleCountryFiltersChange = useCallback((values: string[]) => {
    setLocalCountryFilters(values);
  }, []);

  const handleImpactLevelsChange = useCallback((values: string[]) => {
    // Filter to valid impact levels
    const validLevels = values.filter((v): v is ImpactLevel =>
      v === 'low' || v === 'medium' || v === 'high'
    );
    setLocalImpactLevels(validLevels);
  }, []);

  const handleSelectAllCountries = useCallback(() => {
    const visibleValues = new Set(filteredCountryOptionsDisplay.map((o) => o.value));
    setLocalCountryFilters((prev) => {
      const combined = new Set([...prev, ...visibleValues]);
      return Array.from(combined);
    });
  }, [filteredCountryOptionsDisplay]);

  const handleClearAllCountries = useCallback(() => {
    const visibleValues = new Set(filteredCountryOptionsDisplay.map((o) => o.value));
    setLocalCountryFilters((prev) => prev.filter((v) => !visibleValues.has(v)));
  }, [filteredCountryOptionsDisplay]);

  const handleEventToggle = useCallback((providerEventId: string) => {
    setSelectedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(providerEventId)) {
        next.delete(providerEventId);
      } else {
        next.add(providerEventId);
      }
      return next;
    });
  }, []);

  const handleToggleShowSelected = useCallback(() => {
    // If toggling ON and event search has text, clear the search
    if (!showSelectedOnly && eventSearchQuery.trim().length > 0) {
      setEventSearchQuery('');
    }
    setShowSelectedOnly((prev) => !prev);
  }, [showSelectedOnly, eventSearchQuery]);

  const handleToggleShowSelectedCountries = useCallback(() => {
    // If toggling ON and country search has text, clear the search
    if (!showSelectedCountriesOnly && searchQuery.trim().length > 0) {
      setSearchQuery('');
    }
    setShowSelectedCountriesOnly((prev) => !prev);
  }, [showSelectedCountriesOnly, searchQuery]);

  const handleTrackSelected = useCallback(() => {
    // Get selected events that aren't already tracked
    const eventsToTrack = selectableEvents.filter((e) => {
      if (!e.providerEventId) return false;
      if (trackedEventIds.has(e.providerEventId)) return false;
      if (!selectedEventIds.has(e.providerEventId)) return false;
      return true;
    });

    // Dedupe before sending
    const deduped = dedupeByProviderEventId(eventsToTrack);

    if (deduped.length > 0) {
      onTrackEvents(deduped);
    }
  }, [selectableEvents, trackedEventIds, selectedEventIds, onTrackEvents]);

  const handleTrackAllVisible = useCallback(() => {
    // Get all visible events that aren't already tracked
    const eventsToTrack = filteredEvents.filter((e) => {
      if (!e.providerEventId) return false;
      if (trackedEventIds.has(e.providerEventId)) return false;
      return true;
    });

    // Dedupe before sending
    const deduped = dedupeByProviderEventId(eventsToTrack);

    if (deduped.length > 0) {
      onTrackEvents(deduped);
    }
  }, [filteredEvents, trackedEventIds, onTrackEvents]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Empty state - no events at all
  if (selectableEvents.length === 0) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        placement="top-center"
        scrollBehavior="inside"
        size="lg"
      >
        <ModalContent>
          <ModalHeader>
            <span className="text-lg font-semibold">Add Economic Events</span>
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <p className="text-default-500 text-center">No economic events available</p>
              <p className="text-sm text-default-400 text-center">
                Events will appear here when the API returns data for this date
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top-center"
      scrollBehavior="inside"
      size="2xl"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <span className="text-lg font-semibold">Add Economic Events</span>
          <span className="text-sm text-default-500 font-normal">
            {formattedSessionDate
              ? `Events for ${formattedSessionDate}`
              : 'Select events to track for this session'}
          </span>
        </ModalHeader>

        <ModalBody className="gap-4">
          {/* Unified Search & Filters Card */}
          <Card shadow="sm" className="shrink-0">
            <CardHeader className="flex flex-row items-center gap-2 py-2 flex-wrap">
              {/* Event Search - always visible */}
              <Input
                placeholder="Search events..."
                value={eventSearchQuery}
                onValueChange={setEventSearchQuery}
                isClearable
                onClear={() => setEventSearchQuery('')}
                size="sm"
                classNames={{
                  base: 'flex-1 min-w-[140px]',
                  input: 'text-base',
                }}
                startContent={<Search size={18} />}
              />
              {/* Filter Summary */}
              <FilterSummary
                impactLevels={localImpactLevels}
                countryFilters={localCountryFilters}
              />
              {/* Expand/Collapse */}
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => setIsFiltersCollapsed((prev) => !prev)}
                aria-label={isFiltersCollapsed ? 'Expand filters' : 'Collapse filters'}
              >
                {isFiltersCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
              </Button>
            </CardHeader>
            {!isFiltersCollapsed && (
              <CardBody className="gap-4 pt-0">
                {/* Impact Level Section */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-default-600">Impact Level</p>
                  <CheckboxGroup
                    orientation="horizontal"
                    value={localImpactLevels}
                    onValueChange={handleImpactLevelsChange}
                  >
                    {IMPACT_LEVEL_OPTIONS.map((option) => (
                      <Checkbox key={option.value} value={option.value} size="sm">
                        {option.label}
                      </Checkbox>
                    ))}
                  </CheckboxGroup>
                </div>

                {/* Country Section */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-default-600">Countries</p>
                    {localCountryFilters.length > 0 && (
                      <Button
                        size="sm"
                        variant={showSelectedCountriesOnly ? 'solid' : 'flat'}
                        color={showSelectedCountriesOnly ? 'primary' : 'default'}
                        onPress={handleToggleShowSelectedCountries}
                      >
                        {showSelectedCountriesOnly ? 'Show all' : 'Show selected'}
                      </Button>
                    )}
                  </div>
                  {/* Country Search */}
                  <Input
                    placeholder="Search countries..."
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                    isClearable
                    onClear={() => setSearchQuery('')}
                    size="sm"
                    classNames={{ input: 'text-base' }}
                  />
                  {/* Select All / Clear All */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={handleSelectAllCountries}
                      isDisabled={filteredCountryOptionsDisplay.length === 0}
                    >
                      Select All
                    </Button>
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={handleClearAllCountries}
                      isDisabled={visibleSelectedCountryCount === 0}
                    >
                      Clear All
                    </Button>
                    <span className="text-xs text-default-400 ml-auto">
                      {localCountryFilters.length === 0
                        ? 'All countries'
                        : `${localCountryFilters.length} selected`}
                    </span>
                  </div>
                  {/* Country List */}
                  <ScrollShadow size={5} className="max-h-32" hideScrollBar>
                    {filteredCountryOptionsDisplay.length > 0 ? (
                      <CheckboxGroup
                        value={localCountryFilters}
                        onValueChange={handleCountryFiltersChange}
                      >
                        {filteredCountryOptionsDisplay.map((option) => (
                          <Checkbox key={option.value} value={option.value} size="sm">
                            {option.label}
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    ) : (
                      <p className="text-sm text-default-400 py-2 text-center">
                        {showSelectedCountriesOnly
                          ? 'No countries selected'
                          : 'No countries match your search'}
                      </p>
                    )}
                  </ScrollShadow>
                </div>
              </CardBody>
            )}
          </Card>

          {/* Events List */}
          <Card shadow="sm">
            <CardHeader className="flex flex-row items-center justify-between pb-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Events</p>
                {selectedEventIds.size > 0 && (
                  <Chip size="sm" variant="flat" color="primary">
                    {selectedEventIds.size} selected
                  </Chip>
                )}
              </div>
              {/* Show toggle if there are selections OR if currently in show-selected mode */}
              {(selectedEventIds.size > 0 || showSelectedOnly) && (
                <Button
                  size="sm"
                  variant={showSelectedOnly ? 'solid' : 'flat'}
                  color={showSelectedOnly ? 'primary' : 'default'}
                  onPress={handleToggleShowSelected}
                >
                  {showSelectedOnly ? 'Show all' : 'Show selected'}
                </Button>
              )}
            </CardHeader>
            <CardBody className="pt-2 gap-3">
              {filteredEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <p className="text-default-500 text-center">
                    {showSelectedOnly
                      ? 'No events selected'
                      : eventSearchQuery.trim()
                        ? 'No events match your search'
                        : 'No events match current filters'}
                  </p>
                  {!showSelectedOnly && !eventSearchQuery.trim() && localImpactLevels.length === 0 && (
                    <p className="text-xs text-default-400 text-center">
                      Select at least one impact level to see events
                    </p>
                  )}
                </div>
              ) : (
                <ScrollShadow size={5} className="max-h-64" hideScrollBar>
                  <div className="space-y-1">
                    {filteredEvents.map((event) => {
                      const providerEventId = event.providerEventId ?? '';
                      const isTracked = trackedEventIds.has(providerEventId);
                      const isSelected = selectedEventIds.has(providerEventId);

                      return (
                        <EventListItem
                          key={event.id}
                          event={event}
                          isSelected={isSelected}
                          isTracked={isTracked}
                          onToggle={() => handleEventToggle(providerEventId)}
                          displayTimezone={displayTimezone}
                          timeFormat={timeFormat}
                        />
                      );
                    })}
                  </div>
                </ScrollShadow>
              )}
            </CardBody>
          </Card>
        </ModalBody>

        <ModalFooter className="flex justify-between">
          <Button variant="light" onPress={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              variant="flat"
              onPress={handleTrackAllVisible}
              isDisabled={visibleNonTrackedCount === 0}
            >
              Track All Visible ({visibleNonTrackedCount})
            </Button>
            <Button
              color="primary"
              onPress={handleTrackSelected}
              isDisabled={selectedEventIds.size === 0}
            >
              Track Selected ({selectedEventIds.size})
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddEventsModal;
